import type { Editor, FileLoader, UploadResponse } from "ckeditor5"

import { addMedia } from "@/(admin)/modules/media/services/apiServices"
import { resolveMediaDomainParam } from "@/(admin)/modules/media/utils/media-domain"
import { toImageSrc } from "@/lib/utils"
import { useDomainStore } from "@/stores/use-domain-store"
import type { MediaData } from "@/lib/config/types"

/**
 * CKEditor upload adapter: ảnh kéo-thả / dán / chọn trực tiếp trong editor sẽ
 * được upload lên Strapi media library và chèn vào nội dung.
 *
 * Quy ước folder: LUÔN upload vào THƯ MỤC GỐC (root) của thư viện — truyền
 * `folderId = null` cho `addMedia`. Domain lấy từ site admin đang thao tác
 * (đọc trực tiếp từ zustand store vì adapter chạy ngoài React).
 */
class StrapiUploadAdapter {
  private loader: FileLoader
  private controller: AbortController

  constructor(loader: FileLoader) {
    this.loader = loader
    this.controller = new AbortController()
  }

  async upload(): Promise<UploadResponse> {
    const file = await this.loader.file
    if (!file) {
      throw new Error("Không đọc được tệp để tải lên")
    }

    const domain = resolveMediaDomainParam(useDomainStore.getState().selectedDomain)
    const formData = new FormData()
    formData.append("files", file)

    // folderId = null → thư viện gốc (root), khớp hành vi upload ở "Tất cả tệp".
    const items = await addMedia(formData, null, domain)
    const media = (Array.isArray(items) ? items[0] : items) as MediaData | undefined

    if (!media?.url) {
      throw new Error("Tải ảnh lên thất bại")
    }

    // CKEditor nhận `{ default: url }`, các key số còn lại là width cho srcset.
    const response: UploadResponse = { default: toImageSrc(media.url) }
    const formats = (media.formats || {}) as unknown as Record<string, { url?: string; width?: number } | undefined>
    ;(["thumbnail", "small", "medium", "large"] as const).forEach(key => {
      const fmt = formats[key]
      if (fmt?.url && fmt.width) {
        response[String(fmt.width)] = toImageSrc(fmt.url)
      }
    })

    return response
  }

  abort(): void {
    this.controller.abort()
  }
}

/**
 * Đăng ký adapter với FileRepository của editor. Thêm plugin này vào mảng
 * `plugins` (cùng `ImageUpload`) để bật kéo-thả / dán ảnh tự upload.
 */
export function StrapiUploadAdapterPlugin(editor: Editor): void {
  const fileRepository = editor.plugins.get("FileRepository")
  fileRepository.createUploadAdapter = (loader: FileLoader) => new StrapiUploadAdapter(loader)
}
